import h from "trader-hyperscript";
import {
  Field,
  Form,
  checkBox,
  constantField,
  numberBox,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulaid,
  payment_scheme_id,
  price_adapted_to_lg,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import {
  checkboxesField,
  wrapSelectboxesInPanel,
} from "../../client/checkboxesfield";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import {
  currencyBox,
  singleDueDateCalculationField,
} from "../../client/financialfields";
import {
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import { isNil } from "../../lib/istype";
import {
  allMonths,
  parseMonth,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import { translateMonth } from "../../translations_specific";
import { renderSaveButton } from "../../client/savebutton";
import { savePaymentSchemeSpec } from "../../routes";
import { rpc } from "trader.ts/network";
import * as routes from "../../routes";
import { wrapFieldWithLabel } from "../../lib/wrapfield";
import currency from "currency.js";

type periodicPaymentSchemeForEditing = Omit<periodic_payment_scheme, "id"> & {
  id: payment_scheme_id | null;
};
type nonPeriodicPaymentSchemeForEditing = Omit<
  non_periodic_payment_scheme,
  "id"
> & {
  id: payment_scheme_id | null;
};

export type paymentSchemeForEditing =
  | periodicPaymentSchemeForEditing
  | nonPeriodicPaymentSchemeForEditing;

export function render(opts: {
  payment_scheme: paymentSchemeForEditing;
  allFormulas: { id: formulaid; description: string }[];
  linkedFormulas: formulaid[];
  hasOnlinePayments: boolean;
}): HTMLElement | HTMLElement[] {
  if (opts.payment_scheme.tag === "non_periodic") {
    const form = nonPeriodicPaymentSchemeField(
      {
        hasOnlinePayments: opts.hasOnlinePayments,
        allFormulas: opts.allFormulas,
        linkedFormulas: opts.linkedFormulas,
      },
      opts.payment_scheme,
    );
    return (
      <div>
        {form.render()}
        {renderSaveButton({
          formS: form.s,
          save: (values) => rpc(savePaymentSchemeSpec, undefined, values),
          reload: () => {},
        })}
      </div>
    );
  } else if (opts.payment_scheme.tag === "periodic") {
    const form = periodicPaymentSchemeField(
      {
        hasOnlinePayments: opts.hasOnlinePayments,
        allFormulas: opts.allFormulas,
        linkedFormulas: opts.linkedFormulas,
      },
      opts.payment_scheme,
    );
    return (
      <div>
        {form.render()}
        {renderSaveButton({
          formS: form.s,
          save: async (values) => {
            const res = await rpc(savePaymentSchemeSpec, undefined, values);
            return {
              ...res,
              redirectUrl: routes.payment_schemes_listRoute.link(),
              timeout: 1500,
            };
          },
          reload: false,
        })}
      </div>
    );
  } else {
    return checkAllCasesHandled(opts.payment_scheme);
  }
}

async function depositField(opts: {
  initialValue: null | non_periodic_payment_scheme["deposit"];
  charge_deposit: boolean;
  hasOnlinePayments: boolean;
}): Promise<
  Field<null | {
    amount: currency;
    pay_during_online_enrollment: boolean;
    due_date_calculation: singleDueDateCalculation;
  }>
> {
  if (opts.charge_deposit === false) {
    return constantFieldWithRender(null, [<td></td>, <td></td>, <td></td>]);
  } else {
    return new Form({})
      .addField("amount", [], async () =>
        currencyBox(opts.initialValue?.amount || currency(0), {
          label: "Bedrag voorschot",
        }),
      )
      .addField("pay_during_online_enrollment", [], async () =>
        opts.hasOnlinePayments
          ? wrapFieldWithLabel(
              "Online inschrijving",
              await checkBox({
                class: style.checkbox + style.input_like,
                initialVal:
                  opts.initialValue?.pay_during_online_enrollment || false,
                label: "Betaal voorschot tijdens online inschrijving",
              }),
            )
          : constantField(false),
      )
      .addField("due_date_calculation", [], async () => {
        const def: singleDueDateCalculation = {
          tag: "RelativeToCreationDate" as const,
          days: 3,
        };
        return singleDueDateCalculationField({
          initialVal: opts.initialValue?.due_date_calculation || def,
          label: "Betaaldatum",
        });
      })
      .build({
        render: (fields) => {
          return (
            <div className={style.panel}>
              <div>{fields.amount()}</div>
              <div
                className={style.equal_columns}
                style="align-items: flex-end;"
              >
                <div>{fields.due_date_calculation()}</div>
                <div>{fields.pay_during_online_enrollment()}</div>
              </div>
            </div>
          );
        },
      });
  }
}

function nonPeriodicPaymentSchemeField(
  opts: {
    hasOnlinePayments: boolean;
    allFormulas: { id: formulaid; description: string }[];
    linkedFormulas: formulaid[];
  },
  scheme: nonPeriodicPaymentSchemeForEditing,
): Field<
  nonPeriodicPaymentSchemeForEditing & { linked_formulas: formulaid[] }
> {
  return new Form({})
    .addField("tag", [], () => constantField("non_periodic" as const))
    .addField("id", [], () => constantField(scheme.id))
    .addField("label", [], () =>
      textBox({ label: "Naam", initialVal: scheme.label }),
    )
    .addField("student_can_choose", [], () => studentCanChooseField(scheme))
    .addField("charge_deposit", [], async () =>
      wrapFieldWithLabel(
        "Voorschot",
        await checkBox({
          initialVal: !isNil(scheme.deposit),
          class: style.checkbox + style.input_like,
          label: "Reken voorschot aan",
        }),
      ),
    )
    .addField("deposit", ["charge_deposit"], async ({ charge_deposit }) => {
      return depositField({
        charge_deposit,
        hasOnlinePayments: opts.hasOnlinePayments,
        initialValue: scheme.deposit,
      });
    })
    .addField("rest", [], async () => {
      const form = new Form({})
        .addField("pay_during_online_enrollment", [], async () =>
          opts.hasOnlinePayments
            ? wrapFieldWithLabel(
                "Online inschrijving",
                await checkBox({
                  initialVal:
                    scheme.rest?.pay_during_online_enrollment || false,
                  class: style.checkbox + style.input_like,
                  label: "Betaal hoofdbedrag tijdens online inschrijving",
                }),
              )
            : constantField(false),
        )
        .addField("due_date_calculation", [], async () =>
          singleDueDateCalculationField({
            initialVal: scheme.rest?.due_date_calculation || {
              tag: "RelativeToCreationDate",
              days: 3,
            },
            label: "Betaaldatum hoofdbedrag",
          }),
        )
        .build({
          render: (fields) => (
            <section className={style.panel}>
              <div
                className={style.equal_columns}
                style="align-items: flex-end;"
              >
                {fields.due_date_calculation()}
                {fields.pay_during_online_enrollment()}
              </div>
            </section>
          ),
        });
      return wrapFieldWithLabel("Hoofdbedrag", form);
    })
    .addField("linked_formulas", [], async () => {
      return linkedFormulasField(opts);
    })
    .build({
      render: (fields) => (
        <div>
          <section>
            <div className={style.equal_columns}>
              {fields.label()}
              {fields.student_can_choose()}
            </div>
          </section>

          <section>
            {fields.charge_deposit()}
            {fields.deposit()}
          </section>

          <section>{fields.rest()}</section>

          <section>{fields.linked_formulas()}</section>
        </div>
      ),
    });
}

function linkedFormulasField(opts: {
  allFormulas: { id: formulaid; description: string }[];
  linkedFormulas: formulaid[];
}) {
  const values = opts.allFormulas
    .orderBy((f) => f.description)
    .map((ps) => ps.id);
  const field = checkboxesField({
    initial: opts.linkedFormulas,
    values,
    show: (id) => {
      return opts.allFormulas.find((ps) => ps.id === id)?.description || "";
    },
    addNoFilterOption: false,
    addAllOption: true,
  });
  return {
    ...field,
    render: () => {
      return wrapSelectboxesInPanel("Formules", field.render());
    },
  };
}

function periodicPaymentSchemeField(
  opts: {
    hasOnlinePayments: boolean;
    allFormulas: { id: formulaid; description: string }[];
    linkedFormulas: formulaid[];
  },
  scheme: periodicPaymentSchemeForEditing,
): Field<periodicPaymentSchemeForEditing & { linked_formulas: formulaid[] }> {
  return new Form({})
    .addField("tag", [], () => constantField("periodic" as const))
    .addField("id", [], () => constantField(scheme.id))
    .addField("label", [], () =>
      textBox({ label: "Naam", initialVal: scheme.label }),
    )
    .addField("student_can_choose", [], () => {
      return studentCanChooseField(scheme);
    })
    .addField("periodic_payment_method", [], () => {
      const values = ["direct_debit" as const, "manual" as const];
      return selectBox({
        label: "Betaalmiddel",
        initial: scheme.periodic_payment_method,
        values,
        show: (t) =>
          t === "direct_debit"
            ? "Incasso"
            : t === "manual"
            ? "Manueel"
            : checkAllCasesHandled(t),
      });
    })
    .addField("extra_cost", [], async () => {
      return new Form({})
        .addField(
          "amount",
          [],
          () =>
            currencyBox(scheme.extra_cost?.amount || currency(0), {
              label: "Bedrag toeslag",
            }) as Promise<Field<price_adapted_to_lg>>,
        )
        .addField("vat", [], () =>
          numberBox(scheme.extra_cost?.vat || 0, {
            label: "BTW %",
          }),
        )
        .build({
          render: (fields) => (
            <div className={style.panel}>
              <div className={style.equal_columns_non_responsive}>
                {fields.amount()}
                {fields.vat()}
              </div>
            </div>
          ),
        });
    })
    .addField("charge_deposit", [], () =>
      checkBox({
        initialVal: !isNil(scheme.deposit),
        label: "Voorschot",
        class: style.input_like + style.checkbox,
      }),
    )
    .addField("deposit", ["charge_deposit"], async ({ charge_deposit }) => {
      return depositField({
        charge_deposit,
        hasOnlinePayments: opts.hasOnlinePayments,
        initialValue: scheme.deposit,
      });
    })
    .addField("calculation_tag", [], () => {
      const values = ["period" as const, "months" as const];
      return selectBox({
        initial: scheme.calculation.tag,
        values: values,
        label: "Aanrekening",
        show: (t) =>
          t === "period"
            ? "Elke # maanden"
            : t === "months"
            ? "Bepaalde maanden"
            : checkAllCasesHandled(t),
      });
    })
    .addField(
      "calculation",
      ["calculation_tag"],
      async ({ calculation_tag }) => {
        if (calculation_tag === "period") {
          return new Form({})
            .addField("tag", [], () => constantField("period" as const))
            .addField("period", [], () => {
              const values = [1, 2, 3, 6];
              return selectBox({
                values,
                initial:
                  scheme.calculation.tag === "period"
                    ? scheme.calculation.period
                    : 1,
                label: "Periode",
                show: (n) =>
                  n === 1
                    ? "Elke maand"
                    : "Elke %n maanden".replace("%n", n.toString()),
              });
            })
            .build();
        } else if (calculation_tag === "months") {
          return new Form({})
            .addField("tag", [], () => constantField("months" as const))
            .addField("months", [], async () => {
              const field = checkboxesField({
                initial:
                  scheme.calculation.tag === "months"
                    ? scheme.calculation.months
                    : ["September" as const],
                values: allMonths,
                show: (m) => translateMonth(parseMonth(m)),
                addNoFilterOption: false,
              });
              return {
                ...field,
                render: () => {
                  return wrapSelectboxesInPanel(
                    "Maanden",
                    field.render(),
                  );
                },
              };
            })
            .build();
        } else {
          return checkAllCasesHandled(calculation_tag);
        }
      },
    )
    .addField("linked_formulas", [], async () => {
      return linkedFormulasField(opts);
    })
    .build({
      render: (fields) => (
        <div>
          <section>
            <div className={style.equal_columns}>
              {fields.label()}
              {fields.student_can_choose()}
            </div>
          </section>

          <section>{fields.periodic_payment_method()}</section>

          <section>
            <label for="">{"Toeslag"}</label>
            {fields.extra_cost()}
          </section>

          <section>
            <label for="">{"Voorschot"}</label>
            {fields.charge_deposit()}
            {fields.deposit()}
          </section>

          <section>
            <div className={style.equal_columns}>
              {fields.calculation_tag()}
              {fields.calculation()}
            </div>
          </section>

          <section>{fields.linked_formulas()}</section>
        </div>
      ),
    });
}
async function studentCanChooseField(scheme: {
  student_can_choose: boolean;
}): Promise<Field<boolean>> {
  const field = await checkBox({
    initialVal: scheme.student_can_choose,
    label: "Student kan dit schema kiezen tijdens online inschrijving",
    class: style.checkbox + style.input_like,
  });
  return wrapFieldWithLabel("Online inschrijving", field);
}
